import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomerListMatchingAttribute } from '~/types/api.graphql';
import { getMatchingAttributeLabel, MATCHING_ATTRIBUTE_ORDER } from '~/utils/customerListMatchingRules';

import Dropdown, { DropdownItemProps } from '../Dropdown/Dropdown';

interface MatchingAttributePickerProps {
  attribute?: CustomerListMatchingAttribute;
  identifierAttributes: CustomerListMatchingAttribute[];
  onChange: (attribute: CustomerListMatchingAttribute | undefined) => void;
  // Adds a "Don't use" item, which clears the type
  allowNone?: boolean;
}

/** The type picker of a Customer List column, shared by the upload's rule editor and the agent's mapping form. */
const MatchingAttributePicker = ({
  attribute,
  identifierAttributes,
  onChange,
  allowNone = false,
}: MatchingAttributePickerProps): JSX.Element => {
  const { t } = useTranslation();
  const items = useMemo(() => buildItems(t, identifierAttributes, allowNone), [t, identifierAttributes, allowNone]);
  const selectedIndex = attribute ? items.findIndex((item) => item.value === attribute) : undefined;

  return (
    <Dropdown
      placeholder={t('components.matchingAttributePicker.placeholder')}
      items={items}
      selectedIndex={selectedIndex}
      onSelectionChange={(selection) => {
        const index = Number(Array.isArray(selection) ? selection[0] : selection);
        onChange(items[index]?.value);
      }}
    />
  );
};

// Identifiers first, under their own heading, as they are what a rule needs to match a single person
const buildItems = (
  t: (key: string, options?: Record<string, string>) => string,
  identifierAttributes: CustomerListMatchingAttribute[],
  allowNone: boolean,
): DropdownItemProps<CustomerListMatchingAttribute>[] => {
  const identifierSection = t('components.matchingAttributePicker.identifierSection');
  const combineSection = t('components.matchingAttributePicker.combineSection');
  const isIdentifier = (attribute: CustomerListMatchingAttribute) => identifierAttributes.includes(attribute);
  const attributes = [
    ...MATCHING_ATTRIBUTE_ORDER.filter(isIdentifier),
    ...MATCHING_ATTRIBUTE_ORDER.filter((attribute) => !isIdentifier(attribute)),
  ];
  const attributeItems = attributes.map((attribute) => {
    const label = getMatchingAttributeLabel(attribute);
    return {
      // "Use as X" in the menu, just "X" once picked
      text: t('components.matchingAttributePicker.useAs', { attribute: label }),
      selectedText: label,
      section: isIdentifier(attribute) ? identifierSection : combineSection,
      value: attribute,
    };
  });
  return allowNone ? [{ text: t('components.matchingAttributePicker.none') }, ...attributeItems] : attributeItems;
};

export default MatchingAttributePicker;
